'use client';

import { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const zones = [
{
  region: 'Vlaams-Brabant',
  icon: 'MapPinIcon',
  cities: [
  'Leuven', 'Vilvoorde', 'Halle', 'Zaventem', 'Tervuren', 'Overijse',
  'Asse', 'Grimbergen', 'Mechelen', 'Diest', 'Tienen', 'Aarschot',
  'Haacht', 'Boortmeerbeek', 'Kampenhout', 'Steenokkerzeel', 'Zemst',
  'Machelen', 'Diegem', 'Sterrebeek', 'Wezembeek-Oppem', 'Kraainem',
  'Wemmel', 'Meise', 'Londerzeel', 'Kapelle-op-den-Bos', 'Liedekerke',
  'Ternat', 'Dilbeek', 'Sint-Pieters-Leeuw', 'Beersel', 'Linkebeek',
  'Rhode-Sint-Geneses', 'Drogenbos', 'Hoeilaart', 'Huldenberg'],

  highlight: true
},
{
  region: 'Oost-Vlaanderen',
  icon: 'MapIcon',
  cities: [
  'Gent', 'Aalst', 'Sint-Niklaas', 'Dendermonde', 'Lokeren',
  'Wetteren', 'Zottegem', 'Ronse', 'Ninove', 'Geraardsbergen',
  'Oudenaarde', 'Kruishoutem', 'Zele', 'Hamme', 'Buggenhout',
  'Lebbeke', 'Wichelen', 'Berlare', 'Laarne', 'Destelbergen'],

  highlight: true
},
{
  region: 'West-Vlaanderen',
  icon: 'GlobeEuropeAfricaIcon',
  cities: [
  'Brugge', 'Kortrijk', 'Oostende', 'Roeselare', 'Ieper',
  'Torhout', 'Tielt', 'Waregem', 'Harelbeke', 'Deinze',
  'Menen', 'Wevelgem', 'Zwevegem', 'Poperinge', 'Veurne',
  'Diksmuide', 'Gistel', 'Ichtegem', 'Pittem', 'Wingene'],

  highlight: false
},
{
  region: 'Provincie Antwerpen',
  icon: 'BuildingOffice2Icon',
  cities: [
  'Antwerpen', 'Mechelen', 'Turnhout', 'Lier', 'Herentals',
  'Mol', 'Geel', 'Boom', 'Willebroek', 'Kontich',
  'Mortsel', 'Edegem', 'Aartselaar', 'Rumst', 'Puurs',
  'Sint-Amands', 'Bornem', 'Beveren', 'Temse', 'Kruibeke'],

  highlight: false
},
{
  region: 'Limburg',
  icon: 'MapPinIcon',
  cities: [
  'Hasselt', 'Genk', 'Sint-Truiden', 'Tongeren', 'Beringen',
  'Lommel', 'Heusden-Zolder', 'Bilzen', 'Maasmechelen', 'Lanaken',
  'Maaseik', 'Peer', 'Bree', 'Hamont-Achel', 'Neerpelt',
  'Overpelt', 'Leopoldsburg', 'Herk-de-Stad', 'Diepenbeek', 'Zonhoven'],

  highlight: false
}];


export default function ZonesSection() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('active');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal-up, .section-line');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="zones"
      className="px-5 py-5"
      style={{ background: 'rgba(255,255,255,0.5)' }}>

      <div className="max-w-7xl mx-auto">
        <div className="section-line mb-6" />
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.4em] mb-2 block reveal-up" style={{ color: '#16A34A' }}>
              Werkgebied
            </span>
            <h2
              className="font-display leading-tight reveal-up"
              style={{ fontSize: 'clamp(1.5rem,3vw,2.2rem)', fontWeight: 700, color: '#1A2B3C' }}>

              Ons werkgebied
            </h2>
          </div>
          <div
            className="glass-card flex items-center gap-3 px-4 py-2 rounded-full reveal-up"
            style={{ border: '1px solid rgba(232,96,10,0.2)' }}>

            <Icon name="TruckIcon" size={14} style={{ color: '#16A34A' }} />
            <span className="text-xs font-medium" style={{ color: '#4A6580' }}>
              Heel Vlaanderen & Vlaams-Brabant
            </span>
          </div>
        </div>

        {/* Zone grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
          {zones.map((zone) =>
          <div
            key={zone.region}
            className="glass-card rounded-xl p-4 reveal-up"
            style={{
              border: zone.highlight ?
              '1px solid rgba(30,111,217,0.25)' :
              '1px solid rgba(0,0,0,0.07)',
              background: zone.highlight ? 'rgba(30,111,217,0.04)' : 'rgba(255,255,255,0.85)'
            }}>

              <div className="flex items-center gap-2 mb-3">
                <div
                className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{
                  background: zone.highlight ? 'rgba(30,111,217,0.12)' : 'rgba(0,0,0,0.05)'
                }}>

                  <Icon
                  name={zone.icon as any}
                  size={14}
                  style={{ color: zone.highlight ? '#16A34A' : '#7A95AD' }} />

                </div>
                <div>
                  <h3 className="font-display font-semibold text-sm" style={{ color: '#1A2B3C' }}>{zone.region}</h3>
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {zone.cities.map((city) =>
              <span
                key={city}
                className="zone-pill px-2 py-0.5 rounded-full text-xs font-medium cursor-default"
                style={{
                  background: 'rgba(30,111,217,0.06)',
                  border: '1px solid rgba(30,111,217,0.12)',
                  color: '#4A6580',
                  fontSize: '0.7rem'
                }}>

                    {city}
                  </span>
            )}
              </div>
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <p className="text-center mt-6 text-sm reveal-up" style={{ color: '#7A95AD' }}>
          Staat uw gemeente niet in de lijst?{' '}
          <a href="tel:+32496538283" className="font-semibold transition-colors" style={{ color: '#16A34A' }}>
            Bel ons
          </a>{' '}
          — wij dekken waarschijnlijk uw zone.
        </p>
      </div>
    </section>);

}